import { Coins } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { ErrorNotice, PanelCard, SkeletonLines } from './primitives';
import { CHAINS, type ChainId, type RecommendedFees } from '@/lib/mempool';
import { cn } from '@/lib/utils';

const FEE_TARGETS: { key: keyof RecommendedFees; label: string; hint: string }[] = [
  { key: 'fastestFee', label: 'Next block', hint: '~10 min' },
  { key: 'halfHourFee', label: 'High priority', hint: '~30 min' },
  { key: 'hourFee', label: 'Medium priority', hint: '~1 hour' },
  { key: 'economyFee', label: 'Economy', hint: 'slow' },
  { key: 'minimumFee', label: 'Minimum relay', hint: 'floor' },
];

export function FeesCard({
  chainId,
  query,
}: {
  chainId: ChainId;
  query: UseQueryResult<RecommendedFees>;
}) {
  const chain = CHAINS[chainId];
  const { data, isPending, isError, error } = query;

  return (
    <PanelCard
      icon={<Coins className={`size-4 ${chain.classes.text}`} />}
      title="Fee Estimates"
      action={<span className="font-mono text-xs text-muted-foreground">sat/vB</span>}
    >
      {isPending ? (
        <SkeletonLines rows={5} />
      ) : isError ? (
        <ErrorNotice message={error.message} />
      ) : (
        <div className="divide-y">
          {FEE_TARGETS.map(({ key, label, hint }, i) => (
            <div key={key} className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
              <span className="text-sm">
                <span className={cn(i === 0 && 'font-medium')}>{label}</span>
                <span className="ml-2 text-xs text-muted-foreground">{hint}</span>
              </span>
              <span
                className={cn(
                  'font-mono text-sm font-medium',
                  i === 0 ? `rounded-md border px-2 py-0.5 ${chain.classes.softBadge}` : 'text-muted-foreground',
                )}
              >
                {data[key]}
              </span>
            </div>
          ))}
        </div>
      )}
    </PanelCard>
  );
}
